import React, { useEffect } from 'react';
import * as Bixby from '$/core';
import { withFormik, FormikProps } from 'formik';
import * as Yup from 'yup';
import InputBlock from '$/components/global/inputBlock';
import BixButton from '$/components/global/bixButton';
import { IIdentifiable } from '$/core/models';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import classnames from 'classnames';
import EmailInput from '$/components/global/emailInput';
import BixCheckbox from '$/components/global/bixCheckbox';

export interface AddClientFormValues {
  clientName: string;
  bixbyClientEmail: string;
  dataSiteEmail: string;
  clientNotes: string;
  subscriptionTier: string;
  emailDomain: string;
  usersCannotLogin: boolean;
}

interface IClientProps {
  subscriptions?: IIdentifiable[];
  submitButtonText: string;
  onSubmit: (values: AddClientFormValues) => Promise<void>;
  isActive: boolean;
  initialValues?: AddClientFormValues;
  forModal?: boolean;
}

const AddClientFormik: React.FC<IClientProps & FormikProps<AddClientFormValues>> = props => {
  const {
    subscriptions,
    errors,
    touched,
    handleChange,
    handleBlur,
    values,
    handleSubmit,
    isSubmitting,
    isValid,
    status,
    handleReset,
    submitButtonText,
    isActive,
    forModal,
  } = props;

  useEffect(() => {
    if (isActive === false) {
      handleReset();
    }
  }, [handleReset, isActive]);

  return (
    <form onSubmit={handleSubmit} className={classnames({ for_modal: forModal })}>
      <InputBlock hasError={!!errors.clientName && !!touched.clientName} title="Client Name" required>
        <input
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          id="clientName"
          name="clientName"
          value={values.clientName}
        />
        {errors.clientName && touched.clientName ? <p className="error">{errors.clientName}</p> : null}
      </InputBlock>
      <InputBlock hasError={!!errors.emailDomain && !!touched.emailDomain} title="Domain" required>
        <input
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          id="emailDomain"
          name="emailDomain"
          value={values.emailDomain}
        />
        {errors.emailDomain && touched.emailDomain ? <p className="error">{errors.emailDomain}</p> : null}
      </InputBlock>
      <InputBlock
        hasError={!!errors.bixbyClientEmail && !!touched.bixbyClientEmail}
        title="Bixby Client Email"
        required>
        <EmailInput
          domain={Bixby.Constants.EMAIL_DOMAIN_BIXBY}
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          id="bixbyClientEmail"
          name="bixbyClientEmail"
          value={values.bixbyClientEmail}
        />
        {errors.bixbyClientEmail && touched.bixbyClientEmail ? (
          <p className="error">{errors.bixbyClientEmail}</p>
        ) : null}
      </InputBlock>
      <InputBlock hasError={!!errors.dataSiteEmail && !!touched.dataSiteEmail} title="Data Site Email" required>
        <EmailInput
          domain={values.emailDomain}
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          id="dataSiteEmail"
          name="dataSiteEmail"
          value={values.dataSiteEmail}
        />
        {errors.dataSiteEmail && touched.dataSiteEmail ? <p className="error">{errors.dataSiteEmail}</p> : null}
      </InputBlock>
      <InputBlock>
        <BixCheckbox
          id="usersCannotLogin"
          checked={values.usersCannotLogin}
          title="Users Cannot Login"
          onChange={handleChange}>
          Users Cannot Login
        </BixCheckbox>
      </InputBlock>
      <InputBlock title="Subscription Tier" required>
        <select name="subscriptionTier" value={values.subscriptionTier} onChange={handleChange} onBlur={handleBlur}>
          <option key={-1} value="" disabled>
            Select a Subscription Tier
          </option>
          {Bixby.Utils.orderBy(subscriptions || [], i => i.name).map((t, ix) => {
            return (
              <option
                key={ix}
                value={(t as Bixby.Models.IIdentifiable).id}
                label={(t as Bixby.Models.IIdentifiable).name}
              />
            );
          })}
        </select>
        {errors.subscriptionTier && touched.subscriptionTier ? (
          <p className="error">{errors.subscriptionTier}</p>
        ) : null}
      </InputBlock>
      <InputBlock hasError={!!errors.clientNotes && !!touched.clientNotes} title="Client Notes">
        <textarea
          onChange={handleChange}
          onBlur={handleBlur}
          id="clientNotes"
          name="clientNotes"
          value={values.clientNotes}
        />
        {errors.clientNotes && touched.clientNotes ? <p className="error">{errors.clientNotes}</p> : null}
      </InputBlock>
      <p className="error">{status}</p>
      {isSubmitting && <div>Loading...</div>}
      <BixButton type="submit" disabled={isSubmitting || !isValid} onClick={handleSubmit}>
        {submitButtonText}
      </BixButton>
    </form>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    subscriptions: state.subscriptionState.data
      ? (Bixby.Utils.orderBy(state.subscriptionState.data, i => i.name) as Bixby.Models.IIdentifiable[])
      : [],
  };
};

const AddClientForm = withFormik<IClientProps, AddClientFormValues>({
  mapPropsToValues: (props: IClientProps) => {
    const valueOrEmpty = (arr?: IIdentifiable[]) => (arr && arr.length > 0 ? Bixby.Utils.first(arr).id : '');
    return props.initialValues
      ? props.initialValues
      : {
          clientName: '',
          bixbyClientEmail: '',
          dataSiteEmail: '',
          clientNotes: '',
          subscriptionTier: valueOrEmpty(props.subscriptions),
          emailDomain: '',
          usersCannotLogin: false,
        };
  },
  validationSchema: Yup.object().shape({
    clientName: Yup.string().required('Name is required.'),
    bixbyClientEmail: Yup.string().required('Bixby Client Email is required.'),
    dataSiteEmail: Yup.string().required('Data Site Email is required.'),
    subscriptionTier: Yup.string().required('Subscription Tier is required'),
    emailDomain: Yup.string()
      .matches(/^(?!.*,).+\..{2,}$/, 'Domain must be a single valid address, e.g. client.com.')
      .required('Email Domain is required.'),
  }),
  handleSubmit: async (values: AddClientFormValues, { props, setSubmitting, setStatus }) => {
    setStatus(undefined);
    try {
      setSubmitting(true);
      values.bixbyClientEmail = `${values.bixbyClientEmail}@${Bixby.Constants.EMAIL_DOMAIN_BIXBY}`;
      values.dataSiteEmail = `${values.dataSiteEmail}@${values.emailDomain}`;
      await props.onSubmit(values);
    } catch (e: any) {
      const message = Bixby.API.getErrorMessage(e);
      setStatus(message);
    }
    setSubmitting(false);
  },
})(AddClientFormik);

export default connect(mapStateToProps)(AddClientForm);
